import { Post } from '../types/post';

export interface User {
  id: number | string;
  username: string;
  isModerator?: boolean;
  isAdmin?: boolean;
  displayName?: string;
  avatar?: string;
}

export interface CommunityMembership {
  role?: 'member' | 'moderator' | 'admin' | 'owner';
}

/**
 * Check if the moderate dropdown should be visible for a post
 */
export function canModeratePost(
  post: Post,
  currentUser: User | null,
  communityMembership?: CommunityMembership
): boolean {
  // Always show in dev mode
  if (import.meta.env.DEV) {
    return true;
  }

  if (!currentUser) {
    return false;
  }

  // Post author can always moderate their own post
  if (post.authorId === currentUser.id.toString()) {
    return true;
  }

  // Site admin can moderate any post
  if (currentUser.isAdmin) {
    return true;
  }

  // Site moderator can moderate any post
  if (currentUser.isModerator) {
    return true;
  }

  // For community posts, check community permissions
  if (post.postType === 'community' && communityMembership) {
    const role = communityMembership.role;
    if (role === 'moderator' || role === 'admin' || role === 'owner') {
      return true;
    }
  }

  return false;
}

/**
 * Get the editor type based on user permissions
 */
export function getEditorType(
  post: Post,
  currentUser: User,
  communityMembership?: CommunityMembership
): 'user' | 'moderator' | 'admin' | 'community_moderator' | 'community_admin' {
  // If user is the author
  if (post.authorId === currentUser.id.toString()) {
    return 'user';
  }

  // Site admin
  if (currentUser.isAdmin) {
    return 'admin';
  }

  // Site moderator
  if (currentUser.isModerator) {
    return 'moderator';
  }

  // Community admin/owner
  if (post.postType === 'community' && communityMembership) {
    const role = communityMembership.role;
    if (role === 'admin' || role === 'owner') {
      return 'community_admin';
    }
    if (role === 'moderator') {
      return 'community_moderator';
    }
  }

  // Default to user
  return 'user';
}

/**
 * Get the pinner type based on user permissions
 */
export function getPinnerType(
  post: Post,
  currentUser: User,
  communityMembership?: CommunityMembership
): 'user' | 'moderator' | 'admin' | 'community_moderator' | 'community_admin' {
  return getEditorType(post, currentUser, communityMembership);
}

/**
 * Get the badge text for edited posts
 */
export function getEditedBadgeText(editedByType?: string): string {
  switch (editedByType) {
    case 'user':
      return '(edited)';
    case 'moderator':
      return '(edited by mod)';
    case 'admin':
      return '(edited by admin)';
    case 'community_moderator':
      return '(edited by community moderator)';
    case 'community_admin':
      return '(edited by community admin)';
    default:
      return '(edited)';
  }
}

/**
 * Get the badge text for pinned posts
 */
export function getPinnedBadgeText(pinnedByType?: string): string {
  switch (pinnedByType) {
    case 'user':
      return 'Pinned';
    case 'moderator':
      return 'Pinned by Mod';
    case 'admin':
      return 'Pinned by Admin';
    case 'community_moderator':
      return 'Pinned by Community Mod';
    case 'community_admin':
      return 'Pinned by Community Admin';
    default:
      return 'Pinned';
  }
}
